import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Node, NodeLink, RootLayoutRect } from "../util/nodeTypes";
import { validateNode } from "../util/graphValidator";
import { useNameFormatter } from "../util/NameFormatter";
import Line from "./svg/Line";
import SVGNodeScene from "./svg/SVGNodeScene";
import SVGNodeAsset from "./svg/SVGNodeAsset";
import { detectTypeForAssetNode } from "../util/detectAssetType";

type GreenEdge = { fromContentId: number; toContentId: number };

const center = (n: Node) =>
  n.type === "scene"
    ? { x: n.x + SVGNodeScene.WIDTH / 2, y: n.y + SVGNodeScene.HEIGHT / 2 }
    : { x: n.x + SVGNodeAsset.WIDTH / 2, y: n.y + SVGNodeAsset.HEIGHT / 2 };

type Props = {
  nodes: Node[];
  layout: RootLayoutRect;
  links: NodeLink[];
  greenEdges: GreenEdge[];
  newButtonContentIds: number[];
  involvedContentIds: number[];
  detachedNames: string[];
  // "Locate" mode (e.g. hovering a source candidate): halo these nodes without
  // implying any change.
  highlightContentIds?: number[];
};

// Fit the whole graph with margin (same approach as the stats graph) so the
// preview never overflows its box.
const PAD = 140;
// App charte green (tailwind.config green-500 / btn-primary-fill / scene "+").
const GREEN = "#00c889";
const GREEN_DARK = "#007852"; // green-800, for readable label text

const RelinkPreviewGraph: React.FC<Props> = ({
  nodes,
  layout,
  links,
  greenEdges,
  newButtonContentIds,
  involvedContentIds,
  detachedNames,
  highlightContentIds = [],
}) => {
  const { t } = useTranslation();
  const { getHumanizedNodeNameFromNode } = useNameFormatter();

  const involved = new Set(involvedContentIds.map(Number));
  const newButtons = new Set(newButtonContentIds.map(Number));
  const nodeByContentId = (id: number) => nodes.find((n) => Number(n.contentId) === Number(id));

  const svgRef = useRef<SVGSVGElement>(null);

  // Initial frame = bounding box around the *involved* nodes (so the changed
  // scenes start large/readable) with generous margin for labels/pills; falls
  // back to the whole graph when fewer than two nodes are involved.
  // Locate mode (highlight): frame the highlighted node itself so it stays
  // readable on large scenarios — the wide margin keeps its neighborhood visible.
  const baseViewBox = useMemo(() => {
    const highlightSet = new Set(highlightContentIds.map(Number));
    const highlightNodes = nodes.filter((n) => highlightSet.has(Number(n.contentId)));
    const involvedNodes = nodes.filter((n) => involved.has(Number(n.contentId)));
    const pool =
      highlightNodes.length > 0
        ? highlightNodes
        : involvedNodes.length >= 2
        ? involvedNodes
        : nodes;
    if (pool.length === 0) {
      return { x: 0, y: 0, w: layout.width + PAD, h: layout.height + PAD };
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    pool.forEach((n) => {
      const w = n.type === "scene" ? SVGNodeScene.WIDTH : SVGNodeAsset.WIDTH;
      const h = n.type === "scene" ? SVGNodeScene.HEIGHT : SVGNodeAsset.HEIGHT;
      minX = Math.min(minX, n.x);
      minY = Math.min(minY, n.y);
      maxX = Math.max(maxX, n.x + w);
      maxY = Math.max(maxY, n.y + h);
    });
    // Margin around the region: room for the "new button" pill, or (locate
    // mode) enough surrounding context to tell WHERE the scene sits.
    const m = highlightNodes.length > 0 ? 400 : 160;
    // inner <g> is shifted by PAD/2, so node coords live at n.x + PAD/2
    return {
      x: minX + PAD / 2 - m,
      y: minY + PAD / 2 - m,
      w: maxX - minX + 2 * m,
      h: maxY - minY + 2 * m,
    };
  }, [nodes, involvedContentIds, highlightContentIds, layout]);

  const [vb, setVb] = useState(baseViewBox);
  // Reframe whenever the simulated change (and thus baseViewBox) changes.
  useEffect(() => setVb(baseViewBox), [baseViewBox]);

  const zoomBounds = useMemo(() => ({ minW: 160, maxW: (layout.width + PAD) * 2.5 }), [layout]);

  // Wheel-zoom anchored on the cursor (native non-passive listener so we can
  // preventDefault and not scroll the modal).
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return undefined;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const rect = svg.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width;
      const py = (e.clientY - rect.top) / rect.height;
      const factor = e.deltaY > 0 ? 1.12 : 0.89;
      setVb((v) => {
        const nw = Math.min(Math.max(v.w * factor, zoomBounds.minW), zoomBounds.maxW);
        const nh = nw * (v.h / v.w);
        return { x: v.x + px * v.w - px * nw, y: v.y + py * v.h - py * nh, w: nw, h: nh };
      });
    };
    svg.addEventListener("wheel", handler, { passive: false });
    return () => svg.removeEventListener("wheel", handler);
  }, [zoomBounds]);

  // Pan: track movement on the window (not the svg) so dragging keeps working
  // even when the cursor leaves the preview box.
  const onMouseDown = (e: React.MouseEvent) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const scaleX = vb.w / rect.width;
    const scaleY = vb.h / rect.height;
    let last = { x: e.clientX, y: e.clientY };
    const move = (ev: MouseEvent) => {
      const dx = (ev.clientX - last.x) * scaleX;
      const dy = (ev.clientY - last.y) * scaleY;
      last = { x: ev.clientX, y: ev.clientY };
      setVb((v) => ({ ...v, x: v.x - dx, y: v.y - dy }));
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const viewBox = `${vb.x} ${vb.y} ${vb.w} ${vb.h}`;

  return (
    <div className="h-full flex flex-col">
      <div
        className="relative flex-1 min-h-0 overflow-hidden"
        style={{ backgroundColor: "#fafafa", borderRadius: 12, border: "1px solid #eee" }}
      >
        <svg
          ref={svgRef}
          viewBox={viewBox}
          preserveAspectRatio="xMidYMid meet"
          className="block w-full h-full cursor-grab active:cursor-grabbing"
          style={{ touchAction: "none" }}
          onMouseDown={onMouseDown}
          onDoubleClick={() => setVb(baseViewBox)}
        >
          <defs>
            <marker
              id="relinkArrowHead"
              markerWidth="6"
              markerHeight="6"
              refX="5"
              refY="3"
              orient="auto"
            >
              <path d="M0,0 L6,3 L0,6 Z" fill={GREEN} />
            </marker>
          </defs>

          <g transform={`translate(${PAD / 2} ${PAD / 2})`}>
            {/* Existing structure, dimmed for context */}
            <g opacity={0.35}>
              {links.map((l, i) => (
                <Line
                  key={`l-${i}`}
                  from={l.from}
                  to={l.to}
                  linksToScene={l.linksToScene}
                  index={l.index}
                  isNotThatSimple={l.isNotThatSimple}
                />
              ))}
            </g>

            {/* Locate halo behind each highlighted node (hover mode) */}
            {highlightContentIds.map((id) => {
              const n = nodeByContentId(id);
              if (!n) return null;
              const c = center(n);
              const r = n.type === "scene" ? 95 : 60;
              return (
                <g key={`hl-${id}`}>
                  <circle cx={c.x} cy={c.y} r={r} fill={GREEN} opacity={0.12} />
                  <circle
                    cx={c.x}
                    cy={c.y}
                    r={r}
                    fill="none"
                    stroke={GREEN}
                    strokeWidth={3}
                    opacity={0.5}
                  />
                </g>
              );
            })}

            {/* "New" halo behind each created button */}
            {newButtonContentIds.map((id) => {
              const n = nodeByContentId(id);
              if (!n) return null;
              const c = center(n);
              return (
                <g key={`halo-${id}`}>
                  <circle cx={c.x} cy={c.y} r={60} fill={GREEN} opacity={0.12} />
                  <circle
                    cx={c.x}
                    cy={c.y}
                    r={60}
                    fill="none"
                    stroke={GREEN}
                    strokeWidth={3}
                    opacity={0.5}
                  />
                </g>
              );
            })}

            {/* The change edges (drawn explicitly, under the nodes) */}
            {greenEdges.map((e, i) => {
              const from = nodeByContentId(e.fromContentId);
              const to = nodeByContentId(e.toContentId);
              if (!from || !to) return null;
              const a = center(from);
              const b = center(to);
              // No arrowhead on edges that land in a new button (it sits right on
              // the button and reads poorly); keep it on edges that reach a scene.
              const showArrow = !newButtons.has(Number(e.toContentId));
              return (
                <line
                  key={`green-${i}`}
                  x1={a.x}
                  y1={a.y}
                  x2={b.x}
                  y2={b.y}
                  stroke={GREEN}
                  strokeWidth={7}
                  strokeLinecap="round"
                  markerEnd={showArrow ? "url(#relinkArrowHead)" : undefined}
                />
              );
            })}

            {/* Nodes — involved at full opacity, the rest dimmed */}
            {nodes.map((n) => {
              // Synthetic (preview-only) nodes use negative ids and are always valid.
              const valid = Number(n.contentId) < 0 ? true : validateNode(n);
              const isInvolved = involved.has(Number(n.contentId));
              const isNewButton = newButtons.has(Number(n.contentId));
              const key =
                n.type === "asset" && n.duplicatedForSceneId
                  ? `${n.modelName}-${n.contentId}-dup${n.duplicatedForSceneId}`
                  : `${n.modelName}-${n.contentId}`;
              return (
                <g key={key} opacity={isInvolved ? 1 : 0.22}>
                  {n.type === "scene" ? (
                    <SVGNodeScene
                      title={getHumanizedNodeNameFromNode(n)}
                      valid={valid}
                      hideInfo
                      x={n.x}
                      y={n.y}
                      details={n.details}
                      sceneType={(n.details as any)?.scene_type}
                      startScene={(n.details as any)?.is_start_scene}
                    />
                  ) : (
                    <SVGNodeAsset
                      type={detectTypeForAssetNode(n)}
                      valid={valid}
                      hideInfo
                      selected={isNewButton}
                      x={n.x}
                      y={n.y}
                      modelName={n.modelName}
                    />
                  )}
                </g>
              );
            })}

            {/* "New button" label as a readable pill */}
            {newButtonContentIds.map((id) => {
              const n = nodeByContentId(id);
              if (!n) return null;
              const c = center(n);
              const label = t("pages.project.edit.relinkPreviewNewButton");
              const w = 14 + label.length * 13;
              const y = c.y + SVGNodeAsset.HEIGHT / 2 + 8;
              return (
                <g key={`lbl-${id}`}>
                  <rect
                    x={c.x - w / 2}
                    y={y}
                    width={w}
                    height={34}
                    rx={17}
                    fill="#ffffff"
                    stroke={GREEN}
                    strokeWidth={2}
                  />
                  <text
                    x={c.x}
                    y={y + 17}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={22}
                    fontWeight={700}
                    fill={GREEN_DARK}
                  >
                    {label}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>
        {/* Navigation hint */}
        <div className="pointer-events-none absolute bottom-2 right-3 text-[11px] text-gray-400 select-none">
          {t("pages.project.edit.relinkPreviewZoomHint")}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-2 flex-shrink-0 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
        {(greenEdges.length > 0 || newButtonContentIds.length > 0) && (
          <span className="flex items-center gap-1">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: GREEN }}
            />
            {t("pages.project.edit.relinkLegendNew")}
          </span>
        )}
        <span className="flex items-center gap-1">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-gray-300" />
          {t("pages.project.edit.relinkLegendExisting")}
        </span>
        {detachedNames.length > 0 && (
          <span className="flex items-center gap-1 text-red-600">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-400" />
            {t("pages.project.edit.relinkLegendDetached")} : {detachedNames.join(", ")}
          </span>
        )}
      </div>
    </div>
  );
};

export default RelinkPreviewGraph;
